import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

/**
 * BarcodeScanner — uses the native `BarcodeDetector` API when available
 * (Chrome Android, Edge, some Safari). Falls back to `getUserMedia` + user-taps-
 * to-photograph if `BarcodeDetector` isn't there. Emits scan onDetected + closes.
 *
 * Product lookup is deliberately NOT done here — this component only returns the
 * raw barcode string. Parent decides whether to lookup via OpenBarcode API,
 * OpenFoodFacts, or just record the number.
 */

interface Props {
  open: boolean
  onClose: () => void
  onDetected: (code: string, format?: string) => void
}

declare global {
  interface Window {
    BarcodeDetector?: {
      new (options?: { formats?: string[] }): {
        detect(source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement): Promise<Array<{ rawValue: string; format: string }>>
      }
      getSupportedFormats?(): Promise<string[]>
    }
  }
}

export function BarcodeScanner({ open, onClose, onDetected }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [supported, setSupported] = useState<boolean>(true)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    let stream: MediaStream | null = null
    let intervalId: number | null = null

    ;(async () => {
      try {
        const hasApi = 'BarcodeDetector' in window
        setSupported(hasApi)
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera not available on this device')
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        })
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
        }
        if (hasApi && window.BarcodeDetector) {
          const detector = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'],
          })
          intervalId = window.setInterval(async () => {
            if (cancelled || !videoRef.current || videoRef.current.readyState < 2) return
            try {
              const codes = await detector.detect(videoRef.current)
              if (codes.length > 0) {
                onDetected(codes[0].rawValue, codes[0].format)
                cancelled = true
                onClose()
              }
            } catch { /* transient */ }
          }, 350)
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e))
      }
    })()

    return () => {
      cancelled = true
      if (intervalId != null) window.clearInterval(intervalId)
      if (stream) stream.getTracks().forEach((t) => t.stop())
    }
  }, [open, onClose, onDetected])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 pt-safe text-white">
            <div className="text-body font-semibold">Scan barcode</div>
            <button type="button" onClick={onClose} className="icon-btn" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }} aria-label="Close scanner">✕</button>
          </div>

          <div className="relative flex-1">
            <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />

            {/* Guide overlay */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="rounded-r-md border-2 border-white/60" style={{ width: '80%', maxWidth: 320, aspectRatio: '3 / 1' }} />
            </div>
          </div>

          <div className="px-4 py-4 pb-safe text-center text-caption text-white/85">
            {err ? err
              : supported ? 'Point at the barcode. Auto-detects when in frame.'
              : 'Barcode detection not supported in this browser — take a photo of the label instead and note the number manually.'}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
